import { Alert, AlertProps, alpha, Box, SxProps, Typography, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import React from 'react';

import theme, { CustomShadows } from '@/theme';

import { getCalculatorSummaryBoxStyles, getCalculatorSummaryItemStyles } from './CalculatorStyles';

interface CalculatorOutputsProps {
  title?: string;
  sx?: SxProps;
  children: React.ReactNode;
}

export const CalculatorOutputs = ({
  title = 'Loan Summary',
  sx = {},
  children,
}: CalculatorOutputsProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 3,
        pl: {
          md: 8,
        },
        pt: {
          xs: 7,
          md: 3,
        },
        flexGrow: 1,
        borderRadius: 6,
        borderBottomLeftRadius: { xs: theme.shape.borderRadius * 6, md: 0 },
        borderTopRightRadius: { xs: 0, md: theme.shape.borderRadius * 6 },
        borderTopLeftRadius: 0,
        boxShadow: (theme.shadows as CustomShadows).d,
        background: `
          radial-gradient(115% 140% at 55% 110%, ${theme.palette.brand.secondary}60 0%, transparent 70%),
          radial-gradient(70% 40% at 60% 10%, ${theme.palette.brand.primary}80 0%, transparent 90%),
          radial-gradient(40% 60% at 90% 10%, ${theme.palette.brand.primary}80 0%, transparent 90%),
          ${theme.palette.purple[900]}
        `,
        maxWidth: {
          xs: `calc(100% - ${theme.spacing(3)} * 2)`,
          md: '65%',
        },
        minWidth: {
          md: '35%',
        },
        ...sx,
      }}
    >
      <Typography
        variant="h3"
        sx={{
          color: theme.palette.common.white,
          textTransform: 'uppercase',
          fontSize: { xs: '1.25rem', md: '1.5rem' },
          fontWeight: 700,
          mb: 3,
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

interface SummaryItem {
  label: string;
  value: string;
}

interface CalculatorSummaryProps {
  items: SummaryItem[];
}

export const CalculatorSummary = ({ items }: CalculatorSummaryProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        paddingInline: 1.5,
        maxWidth: '100%',
        mt: {
          sx: 0,
          md: 2,
        },
      }}
    >
      <Box sx={getCalculatorSummaryBoxStyles()}>
        {items.map((item, idx) => (
          <Box key={idx} sx={getCalculatorSummaryItemStyles(theme)}>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {item.label}
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

interface OutputFieldProps {
  label: string;
  value: React.ReactNode;
  highlight?: boolean;
  sx?: SxProps;
}

export const OutputField = ({ label, value, sx = {}, highlight = false }: OutputFieldProps) => {
  const theme = useTheme();
  return (
    <Box
      component="dl"
      sx={{
        background: highlight
          ? `${theme.palette.gradients.purpleDark}`
          : `${theme.palette.purple[900]}95`,
        color: 'common.white',
        borderRadius: 2,
        p: 1,
        px: 2.5,
        m: 0,
        flexGrow: 1,
        ...sx,
      }}
    >
      <Typography
        variant="small"
        component={'dt'}
        sx={{
          textTransform: 'uppercase',
          color: `${theme.palette.common.white}98`,
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="h4"
        component="dd"
        sx={{
          lineHeight: 1.2,
          m: 0,
          my: 0.5,
          textShadow: highlight ? `0 0 0.5rem ${theme.palette.brand.secondary}` : 'none',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export type GraphicData = {
  label: string;
  value: number;
  color: string;
};

export const OutputGraphic = ({ data }: { data: GraphicData[] }) => {
  const chartData = data.map((item, idx) => ({
    id: idx,
    value: item.value,
    label: item.label,
    color: item.color,
  }));

  return (
    <Box
      sx={{
        alignSelf: 'start',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <PieChart
        series={[
          {
            data: chartData,
            innerRadius: 30,
            outerRadius: 50,
            paddingAngle: 2,
            cornerRadius: 4,
            cx: 65,
            cy: 52,
            valueFormatter: (item) => `$${item.value.toLocaleString()}`,
          },
        ]}
        width={130}
        height={125}
        margin={{ top: 2, right: 0, bottom: 5, left: 0 }}
        sx={{
          '& .MuiPieArc-root': {
            stroke: 'none',
            filter: `drop-shadow(0px 4px 6px ${theme.palette.purple[800]})`,
          },
        }}
        slotProps={{ legend: { hidden: true } }}
      />
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignSelf: 'flex-start', pl: 4 }}
      >
        {chartData.map((item) => (
          <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '3px',
                backgroundColor: item.color,
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" sx={{ color: 'white', fontSize: 12 }}>
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export type ColumnTitle = string | { title: string; subtitle?: string };

export type ComparisonRow = {
  label: string;
  values: string[];
  deltas?: (string | null)[];
};

interface OutputComparisonProps {
  columnTitles: ColumnTitle[];
  rows: ComparisonRow[];
  highlightedColumn?: number;
  summary?: React.ReactNode;
}

export const OutputComparison = ({
  columnTitles,
  rows,
  highlightedColumn,
  summary,
}: OutputComparisonProps) => {
  const theme = useTheme();
  const numValueCols = columnTitles.length;
  // 1 header row + 1 row per data entry (label and values share the same grid row)
  const totalGridRows = 1 + rows.length;
  const rowBorder = `1px solid ${alpha(theme.palette.purple[100], 0.08)}`;

  return (
    <Box component="dl" sx={{ m: 0 }}>
      <Box
        sx={{
          backgroundColor: `${theme.palette.purple[900]}95`,
          borderRadius: 2,
          p: 1.5,
          pb: 1,
          display: 'grid',
          gridTemplateColumns: `1.15fr repeat(${numValueCols}, 1fr)`,
          columnGap: 0,
          rowGap: 0,
          alignItems: 'stretch',
        }}
      >
        {/* Highlighted column tint box — spans all rows */}
        {highlightedColumn != null && (
          <Box
            aria-hidden="true"
            sx={{
              gridColumn: highlightedColumn + 2,
              gridRow: `1 / ${totalGridRows + 1}`,
              backgroundColor: alpha(theme.palette.brand.secondary, 0.1),
              border: `0.1rem solid ${alpha(theme.palette.brand.secondary, 0.45)}`,
              borderRadius: 2,
              pointerEvents: 'none',
              mt: -0.5,
            }}
          />
        )}

        {/* Header row — empty label column, then one header per value column */}
        <Box sx={{ gridColumn: 1, gridRow: 1 }} />
        {columnTitles.map((colTitle, colIdx) => {
          const isHighlighted = colIdx === highlightedColumn;
          const titleText = typeof colTitle === 'string' ? colTitle : colTitle.title;
          const subtitleText = typeof colTitle === 'string' ? undefined : colTitle.subtitle;
          return (
            <Box
              key={`header-${colIdx}`}
              sx={{
                gridColumn: colIdx + 2,
                gridRow: 1,
                py: 0.75,
                pt: 0.5,
                pl: 0,
                pr: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  sx={{
                    color: `${theme.palette.common.white}CC`,
                    textTransform: 'uppercase',
                    fontSize: theme.typography.small.fontSize,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    lineHeight: 1,
                    ml: 2,
                  }}
                >
                  {titleText}
                </Typography>
                {isHighlighted && (
                  <Typography
                    aria-hidden="true"
                    component="span"
                    sx={{
                      color: theme.palette.brand.secondary,
                      fontWeight: 700,
                      fontSize: theme.typography.small.fontSize,
                      lineHeight: 1,
                    }}
                  >
                    ✓
                  </Typography>
                )}
              </Box>
              {subtitleText && (
                <Typography
                  variant="small"
                  sx={{
                    color: alpha(theme.palette.common.white, 0.4),
                    lineHeight: 1.2,
                    mt: 0.25,
                  }}
                >
                  {subtitleText}
                </Typography>
              )}
            </Box>
          );
        })}

        {/* Data rows — label in col 1, values in cols 2+ (same grid row) */}
        {rows.map((row, rowIdx) => {
          const gridRow = rowIdx + 2;
          const isLastRow = rowIdx === rows.length - 1;
          return (
            <React.Fragment key={rowIdx}>
              <Box
                component="dt"
                sx={{
                  gridColumn: 1,
                  gridRow,
                  borderTop: rowBorder,
                  display: 'flex',
                  alignItems: 'start',
                  px: 0,
                  py: 0.75,
                }}
              >
                <Typography
                  variant="tiny"
                  sx={{
                    textTransform: 'uppercase',
                    color: `${theme.palette.purple[100]}99`,
                    lineHeight: 1.2,
                    mt: 0.65,
                  }}
                >
                  {row.label}
                </Typography>
              </Box>
              {row.values.map((value, colIdx) => {
                const delta = row.deltas?.[colIdx] ?? null;
                return (
                  <Box
                    key={colIdx}
                    component="dd"
                    sx={{
                      gridColumn: colIdx + 2,
                      gridRow,
                      pl: 1,
                      pr: 1,
                      pt: 0.5,
                      pb: isLastRow ? 1 : 0.5,
                      m: 0,
                      textAlign: 'right',
                      borderTop: rowBorder,
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        lineHeight: 1.2,
                        my: 0.25,
                        color: theme.palette.common.white,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {value}
                    </Typography>
                    {delta && (
                      <Typography
                        variant="small"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.brand.secondary,
                          lineHeight: 1,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {delta}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </React.Fragment>
          );
        })}
      </Box>

      {summary && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1, px: 0.5 }}>
          <Box
            aria-hidden="true"
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: theme.palette.brand.secondary,
              flexShrink: 0,
              mt: 0.5,
            }}
          />
          <Typography variant="small" sx={{ color: alpha(theme.palette.common.white, 0.6) }}>
            {summary}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export type StackedBarSeries = {
  label: string;
  data: number[];
  color: string;
};

function computeNiceAxisMax(dataMax: number): { max: number; tickMinStep: number } {
  if (dataMax <= 0) return { max: 1_000_000, tickMinStep: 250_000 };
  const target = dataMax * 1.2;
  const rawStep = target / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / magnitude;
  const niceStep =
    norm <= 1
      ? magnitude
      : norm <= 2
        ? 2 * magnitude
        : norm <= 2.5
          ? 2.5 * magnitude
          : norm <= 5
            ? 5 * magnitude
            : 10 * magnitude;
  return {
    max: Math.ceil(target / niceStep) * niceStep,
    tickMinStep: niceStep,
  };
}

const formatAxisValue = (v: number) =>
  v >= 1_000_000
    ? `$${(v / 1_000_000).toFixed(1).replace(/\.0$/, '')}m`
    : v >= 1_000
      ? `$${Math.round(v / 1000)}k`
      : `$${Math.round(v)}`;

const formatSeriesValue = (v: number | null) =>
  v != null ? `$${Math.round(v).toLocaleString()}` : '$0';

export const OutputGraphicStackedBar = ({
  categories,
  series,
  sx = {},
}: {
  categories: string[];
  series: StackedBarSeries[];
  sx?: SxProps;
}) => {
  const theme = useTheme();

  const maxDataValue = categories.reduce((max, _, i) => {
    const stackedTotal = series.reduce((sum, s) => sum + (s.data[i] ?? 0), 0);
    return Math.max(max, stackedTotal);
  }, 0);

  // Memoize on the primitive maxDataValue so yAxis is only a new object when
  // the data actually changes — prevents BarChart internal effects from firing
  // on every render and cascading into an infinite InputBase.setAdornedStart loop.
  const { max: yMax, tickMinStep } = React.useMemo(
    () => computeNiceAxisMax(maxDataValue),
    [maxDataValue]
  );

  const yAxis = React.useMemo(
    () => [
      {
        min: 0,
        max: yMax,
        tickMinStep,
        valueFormatter: formatAxisValue,
        tickLabelStyle: { fill: `${theme.palette.common.white}99` },
      },
    ],
    [yMax, tickMinStep, theme.palette.common.white]
  );

  const seriesKey = series.map((s) => `${s.color}:${s.data.join(',')}`).join('|');
  const chartSeries = React.useMemo(
    () =>
      series.map((s) => ({
        data: s.data,
        label: s.label,
        color: s.color,
        stack: 'total' as const,
        valueFormatter: formatSeriesValue,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [seriesKey]
  );

  return (
    <Box
      sx={{
        alignSelf: 'start',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        ...sx,
      }}
    >
      <Box
        sx={{
          alignSelf: 'start',
          display: 'flex',
          flexDirection: 'column',
          mx: 'auto',
          mb: 1,
        }}
      >
        <BarChart
          xAxis={[
            {
              scaleType: 'band',
              data: categories,
              tickLabelStyle: { fill: theme.palette.common.white },
            },
          ]}
          yAxis={yAxis}
          series={chartSeries}
          width={200}
          height={160}
          margin={{ top: 10, right: 20, bottom: 40, left: 35 }}
          slotProps={{ legend: { hidden: true } }}
          sx={{
            '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
              stroke: `${theme.palette.common.white}40`,
            },
            '& .MuiChartsGrid-line': {
              stroke: `${theme.palette.common.white}20`,
            },
          }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1.5, pl: 4 }}>
          {series.map((s, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '3px',
                  backgroundColor: s.color,
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" sx={{ color: theme.palette.common.white, fontSize: 12 }}>
                {s.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

interface OutputAlertProps {
  children: React.ReactNode;
  severity?: AlertProps['severity'];
  sx?: SxProps;
}

export const OutputAlert = ({ children, severity = 'warning', sx = {} }: OutputAlertProps) => {
  const theme = useTheme();
  return (
    <Alert
      severity={severity}
      sx={{
        mb: 1,
        boxShadow: (theme.shadows as CustomShadows).b,
        background: theme.palette.gradients.purpleLight,
        color: theme.palette.common.black,
        fontSize: theme.typography.small.fontSize,
        '& .MuiAlert-icon': {
          color: theme.palette.brand.primary,
          width: '2rem',
        },
        ...sx,
      }}
    >
      {children}
    </Alert>
  );
};
